import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// AUTH API
// ============================================================================
export const authAPI = {
  // Signup flow
  requestSignupOTP: (data) => api.post('/auth/request-signup-otp', data).then(res => res.data),
  
  signup: (data) => api.post('/auth/signup', data).then(res => res.data),

  // Login flow
  requestLoginOTP: (data) => api.post('/auth/request-login-otp', data).then(res => res.data),
  
  login: (data) => api.post('/auth/login', data).then(res => res.data),

  // Admin signup flow
  requestAdminSignup: (data) => api.post('/auth/admin/request-signup', data).then(res => res.data),
  
  verifyAdminOTP: (data) => api.post('/auth/admin/verify-otp', data).then(res => res.data),
  
  adminSignup: (data) => api.post('/auth/admin/signup', data).then(res => res.data),

  // Profile management
  getProfile: () => api.get('/auth/profile').then(res => res.data),
  
  updateProfile: (data) => api.put('/auth/profile', data).then(res => res.data),
  
  uploadProfilePicture: (formData) => {
    return api.post('/auth/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  changePassword: (data) => api.post('/auth/change-password', data).then(res => res.data),

  // 2FA
  enable2FA: () => api.post('/auth/2fa/enable').then(res => res.data),
  
  verify2FA: (otp) => api.post('/auth/2fa/verify', { otp }).then(res => res.data),
  
  disable2FA: () => api.post('/auth/2fa/disable').then(res => res.data),

  // Token management
  refreshToken: (refreshToken) => 
    api.post('/auth/refresh', { refresh_token: refreshToken }).then(res => res.data),
};

// ============================================================================
// CAREERS API (Public + User)
// ============================================================================
export const careersAPI = {
  // Public job listings
  getJobs: (params) => api.get('/careers/jobs', { params }).then(res => res.data),
  
  getJobByID: (id) => api.get(`/careers/jobs/${id}`).then(res => res.data),

  // User applications (requires auth)
  applyForJob: (jobId, formData) => {
    return api.post(`/careers/jobs/${jobId}/apply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  getMyApplications: () => api.get('/careers/applications').then(res => res.data),
  
  getApplicationByID: (id) => api.get(`/careers/applications/${id}`).then(res => res.data),

  withdrawApplication: (id) => api.delete(`/careers/applications/${id}/withdraw`).then(res => res.data),
};

// ============================================================================
// ADMIN API
// ============================================================================
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats').then(res => res.data),

  // Job management
  createJob: (data) => api.post('/admin/jobs', data).then(res => res.data),
  
  updateJob: (id, data) => api.put(`/admin/jobs/${id}`, data).then(res => res.data),
  
  deleteJob: (id) => api.delete(`/admin/jobs/${id}`).then(res => res.data),
  
  getAllJobs: () => api.get('/admin/jobs').then(res => res.data),

  // Application management
  getAllApplications: (params) => api.get('/admin/applications', { params }).then(res => res.data),
  
  updateApplicationStatus: (id, data) => 
    api.put(`/admin/applications/${id}/status`, data).then(res => res.data),

  // Bulk operations
  bulkUploadResumes: (formData) => {
    return api.post('/admin/bulk-upload-resumes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  uploadSingleResume: (formData) => {
    return api.post('/admin/upload-single-resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  // User management
  getAllUsers: () => api.get('/admin/users').then(res => res.data),

  // Email system
  sendCustomEmail: (data) => api.post('/admin/send-email', data).then(res => res.data),
  
  sendBulkEmail: (data) => api.post('/admin/send-bulk-email', data).then(res => res.data),
  
  getEmailTemplates: () => api.get('/admin/email-templates').then(res => res.data),
  
  getEmailLogs: (params) => api.get('/admin/email-logs', { params }).then(res => res.data),
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('access_token');
};

// Get current user from token
export const getCurrentUser = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch {
    return null;
  }
};

// Logout user
export const logout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
};

export default api;